import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { cn } from '@/shared/lib/utils';

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      // z-[1100]: the sticky top-dock header is z-index 1002 (see
      // top-dock-layout.scss). All shared Radix overlays sit at 1100 so
      // modals/popups always render ABOVE the nav instead of being clipped
      // behind it. Keep this in lock-step with the other shared overlays
      // (drawer/popover/select/dropdown-menu/tooltip).
      'tw-fixed tw-inset-0 tw-z-[1100] tw-bg-black/80 data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0 data-[state=open]:tw-fade-in-0',
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  /**
   * Show the top-right X close button. Default `true`.
   * Set `false` for compact confirmation prompts where Cancel + Confirm
   * buttons in the body are the only exit path (matches legacy
   * DeleteModal which has no close X).
   */
  showCloseButton?: boolean;
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, showCloseButton = true, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        // `tw-border-solid` required because preflight is OFF — UA's
        // default `border-style: inset` was bleeding through and adding
        // a dark/3D outline around the modal. Lifted shadow + slate-100
        // hairline border match the legacy DeleteModal's no-border look
        // (legacy: `border: none; box-shadow: 0 10px 40px rgba(0,0,0,0.15)`).
        // z-[1100]: sit above the sticky top-dock header (z-index 1002) so
        // modals aren't clipped behind the nav. See DialogOverlay note.
        'tw-fixed tw-left-[50%] tw-top-[50%] tw-z-[1100] tw-grid tw-w-full tw-max-w-lg tw-translate-x-[-50%] tw-translate-y-[-50%] tw-gap-4',
        'tw-border tw-border-solid tw-border-slate-100',
        'tw-bg-background tw-p-6',
        'tw-shadow-[0_10px_40px_rgba(0,0,0,0.15)]',
        'tw-duration-200',
        'data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0 data-[state=open]:tw-fade-in-0 data-[state=closed]:tw-zoom-out-95 data-[state=open]:tw-zoom-in-95 data-[state=closed]:tw-slide-out-to-left-1/2 data-[state=closed]:tw-slide-out-to-top-[48%] data-[state=open]:tw-slide-in-from-left-1/2 data-[state=open]:tw-slide-in-from-top-[48%] sm:tw-rounded-xl',
        className,
      )}
      {...props}
    >
      {children}
      {showCloseButton ? (
        <DialogPrimitive.Close
          className={cn(
            'tw-absolute tw-right-4 tw-top-4 tw-inline-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-md',
            'tw-border tw-border-solid tw-border-transparent tw-text-slate-400 tw-transition-colors',
            'hover:tw-bg-slate-100 hover:tw-text-slate-600',
            'focus-visible:tw-outline-none focus-visible:tw-border-sky-300 focus-visible:tw-ring-4 focus-visible:tw-ring-sky-100',
            'disabled:tw-pointer-events-none',
          )}
        >
          <X className="tw-h-4 tw-w-4" />
          <span className="tw-sr-only">Close</span>
        </DialogPrimitive.Close>
      ) : null}
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'tw-flex tw-flex-col tw-space-y-1.5 tw-text-center sm:tw-text-left',
      className,
    )}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'tw-flex tw-flex-col-reverse sm:tw-flex-row sm:tw-justify-end sm:tw-space-x-2',
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('tw-text-lg tw-font-semibold tw-leading-none tw-tracking-tight', className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('tw-text-sm tw-text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
